import type { LineProbeStatsBatch, SessionGrant } from '@core/line-probe';
import {
    Chapter,
    ChatChannel,
    ClientType,
    ExamResultType,
    ExamWithPoints,
    OnDemandStatus,
    Participant,
    PollWithPoints,
    PollWithVotes,
    SubtitlesGeneratorOptions,
    SubtitlesGeneratorStatus,
    SubtitlesTrack,
    SurveyWithPoints,
    WebinarStatus,
    WebinarVisibility
} from '@core/types';

export interface TranslationPatch {
    [key: string]: string;
}

export enum EmailTemplateType {
    AccountVerification = 101,
    AccountPasswordReset = 102,
    AccountManagerInvite = 103,
    AccountInvite = 104,
    AccountInviteReminder = 105,
    AccountUnverifiedDeletionWarning = 106,
    AccountInactiveDeletionWarning = 107,

    WebinarInterest = 201,
    WebinarReminder = 202,
    WebinarInvite = 203,
    WebinarInviteReminder = 204,
    WebinarFollowUp = 205,
    WebinarOnDemandChat = 206,

    EmailList = 301,
    WebinarEmailList = 302
}

export interface EmailTemplate {
    subject: string;
    body: string;
}

export interface EmailStyle {
    template: string;
    paragraphStyle: string;
    headingStyles: {
        [key: string]: string;
    };
    buttonStyle: string;
    codeStyle: string;
    tableStyle: string;
    tableHeaderRowStyle: string;
    tableHeaderCellStyle: string;
    tableBodyRowStyle: string;
    tableBodyCellStyle: string;
    /** Absolute URL of the uploaded or adopted header image; null while the default is used. Only uploadHeaderImage and deleteHeaderImage change it: saving the style ignores it. */
    headerImageUrl?: string | null;
    headerImageAltText?: string;
    /** Returned by get, ignored on save. */
    defaultHeaderImageUrl?: string;
}

export interface DashboardStatisticsData {
    totalInterested?: number;
    lastWeekInterested?: number;
    totalViewers?: number;
    lastWeekViewers?: number;
    totalMinutesWatchedLive?: number;
    totalMinutesWatchedOnDemand?: number;
    totalWebinars?: number;
    totalUpcomingWebinars?: number;
}

export interface PollVoteWeights {
    defaultWeight: number;
    weights: PollVoteWeight[];
}

export interface PollVoteWeight {
    emailAddress: string;
    weight: number;
}

export interface PollWeightsRequest {
    webinarId: string;
    pollId: string;
}

export interface AddPollWeightsRequest {
    webinarId: string;
    pollId: string;
    weights: PollVoteWeight[];
}

export interface ImportPollWeightsRequest {
    webinarId: string;
    pollId: string;
    weights: File;
}

export interface SetDefaultVoteWeightRequest {
    webinarId: string;
    pollId: string;
    weight: number;
}

export interface RemoveVoteWeightRequest {
    webinarId: string;
    pollId: string;
    emailAddress: string;
}

export interface RemovePollRequest {
    webinarId?: string;
    pollId?: string;
}

export interface EditPollRequest extends PollWithPoints {
    webinarId: string;
}

export interface AddPollRequest extends PollWithPoints {
    webinarId: string;
}

export enum SlideDeckStatus {
    None = 0,
    Queued = 1,
    Processing = 2,
    Ready = 3,
    Error = 4
}

export interface SlideDeck {
    status: SlideDeckStatus;
    filename: string | null;
    slides: Slide[];
    totalSlides: number;
}

export interface Slide {
    url: string;
    type: SlideType;
}

export enum SlideType {
    Image = 0,
    CallToAction = 1
}

export interface UploadSlideDeckRequest {
    slideDeck: File;
}

export interface UploadEmailHeaderImageRequest {
    file: File;
}

export interface RemoveSlideDeckResponse {
    message?: string;
    webinarId?: string;
}

export interface RemoveSlideDeckRequest {
    webinarId: string;
}

export interface BaseApiResponse {
    errorCode?: string;
    message?: string;
    statusCode: number;
    success: boolean;
}

export interface ApiResponse<TData> extends BaseApiResponse {
    data?: TData;
}

export interface Tier {
    tierId: string;
    name: string;
    numberOfConcurrentChannels: number;
    duration: string;
    durationBeforeStart: string;
    durationAfterEnd: string;
    defaultDuration: string;
}

export interface VindralChannelInformation {
    channelId?: string;
    authorizationToken?: string;
}

export interface WebinarData {
    webinarId: string;
    title: string;
    description: string;
    nameOfHost: string;
    tags: string[];
    broadcastDateTime: string;
    plannedDuration: string;
    tierId?: string;
}

export interface EditWebinarData extends WebinarData {
    clientReference?: string;
}

export interface WebinarDataWithAssets extends WebinarData {
    status: WebinarStatus;
    startedOn?: string;
    endedOn?: string;
    actualDuration?: string;
    assets: WebinarAssets;
    /** A test webinar: at most 15 min and 5 viewers, deleted 24 h after the planned start, not billed. Set at creation, never changed. */
    isTest: boolean;
    /** Whether assets.profileImage is the webinar's own image rather than the account's default it falls back to. */
    hasOwnProfileImage: boolean;
}

export enum WatchLinkStatus {
    Live,
    OnDemand,
    AccessDenied,
    Unavailable,
    Expired,
    Preparing
}

export interface WebinarDataWatchLink extends WebinarDataWithAssets {
    watchLinkStatus: WatchLinkStatus;
}

export interface WebinarDataWithStatus extends WebinarDataWithAssets {
    canWatch: boolean;
    canWatchCountdown: number;
    canShowInterest: boolean;
    isInterested: boolean;
    isPublished: boolean;
    visibility: WebinarVisibility;
    requiresAccountVerification: boolean;
    isOnDemand: boolean;
    isRestricted: boolean;
    canViewAnonymously: boolean;
}

export interface WebinarAssets {
    profileImage?: ImageAsset;
    bannerImage?: ImageAsset;
}

export interface ImageAsset {
    imageUrl: string;
    width: number;
    height: number;
    /** Text alternative for an image that carries meaning; none means decorative (alt=""). Cleared by a new upload. */
    altText?: string | null;
}

export interface FollowUp {
    followUpId: string;
    title: string;
    bccOwner: boolean;
    minimumOnlinePercentage: number;
    minimumPollPoints: number;
    minimumPollVotes: number;
    minimumExamPoints: number;
    minimumExamAnswers: number;
    minimumSurveyAnswers: number;
}

export interface FollowUpStatistics {
    followUpId: string;
    webinarId: string;
    title: string;
    bccOwner: boolean;
    processedFollowUps: number;
}

export enum EmailStatusType {
    Queued = 0,
    Sending = 1,
    Sent = 2,
    Failed = 3
}

export enum EmailPriority {
    Low = 0,
    Medium = 1,
    High = 2
}

export interface EmailStatus {
    status: EmailStatusType;
    priority: EmailPriority;
    createdAt: string;
    sentAt?: string;
    failedAt?: string;
    responseMessage: string;
}

export interface ProcessedFollowUpParticipantData {
    followUpId: string;
    emailId: string;
    emailStatus: EmailStatus;
    createdAt: string;
    onlinePercentage: number;
    pollVotes: number;
    pollPoints: number;
    examPoints: number;
    examAnswers: number;
    surveyAnswers: number;
    participantId: string;
    participant: Participant;
    participantName: string;
}

export interface FollowUpWithEmailTemplate {
    followUp: FollowUp;
    emailTemplate: EmailTemplate;
    renderedEmailTemplate?: EmailTemplate;
}

export enum WebinarAttachmentType {
    File = 0
}

export enum WebinarAttachmentVisibility {
    Public = 0,
    Authenticated = 1,
    Private = 2
}

export interface WebinarAttachment {
    attachmentId: string;
    title: string;
    path: string;
    visibility: WebinarAttachmentVisibility;
    type: WebinarAttachmentType;
}

export enum InviteEmailStatus {
    NotSent = 0,
    Queued = 1,
    Sent = 2,
    Error = 3,
    Skipped = 4,
    Blacklisted = 5
}

export interface InvitedParticipant extends AccountProfile {
    inviteEmailStatus: InviteEmailStatus;
    reminderEmailStatus: InviteEmailStatus;
    inviteErrorMessage: string;
    reminderErrorMessage: string;
    invitedAt: string;
    watchLink: string;
    linkCode: string;
    inviteEmailId?: string;
    reminderEmailId?: string;
}

export interface WebinarInviteSettings {
    autoSendInvite: boolean;
    autoSendReminder: boolean;
    reminderHoursBeforeWebinar: number;
}

export interface InterestedAccount {
    firstName?: string;
    lastName?: string;
    nickname: string;
    companyName?: string;
    countryCode?: string;
    emailAddress: string;
    accountId: string;
    shownInterestOn: string;
}

export interface ShowInterestRequest {
    webinarId: string;
}

export interface WebinarContentSummary extends WebinarDataWithAssets {
    isPublished: boolean;
    visibility: WebinarVisibility;
    numberOfChapters: number;
    numberOfPolls: number;
    numberOfAttachments: number;
    numberOfFollowUps: number;
    numberOfSlides: number;
    numberOfInterested: number;
    numberOfOnlineParticipants: number;
    numberOfLiveParticipants: number;
    numberOfOnDemandParticipants: number;
    hasBooking: boolean;
    surveyStatus: QuestionStatus;
    numberOfSurveyQuestions?: number;
    examStatus: QuestionStatus;
    numberOfExamQuestions?: number;
    securityStatus: SecurityStatus;
    isOnDemandAvailable: boolean;
    onDemandStatus: OnDemandStatus;
    onDemandEnabled: boolean;
    clientReference?: string;
}

export interface WebinarSecuritySettings {
    singleViewerSession: boolean;
    requireVerifiedAccounts: boolean;
    allowEmailAddresses: string[];
    disallowEmailAddresses: string[];
    allowAnonymousViewers: boolean;
    anonymousViewerAuthorizationCode: string;
}

export interface IsAllowedResponse {
    emailAddress: string;
    isAllowed: boolean;
}

export enum SecurityStatus {
    NotEnabled = 0,
    Enabled = 1
}

export enum QuestionStatus {
    NotEnabled = 0,
    NotValid = 1,
    Enabled = 2
}

export interface EndWebinarResponse {
    message?: string;
    webinarId?: string;
}

export interface CancelWebinarResponse {
    webinarId?: string;
    message?: string;
}

export interface CategoryResponse {
    message?: string;
}

export interface AddCategoryRequest {
    categories?: string[];
}

export interface ChangePasswordRequestDto {
    currentPassword?: string | null;
    newPassword?: string | null;
    confirmedPassword?: string | null;
}

export interface EditUserProfileDto {
    firstName?: string | null;
    lastName?: string | null;
    nickname?: string | null;
    companyName?: string | null;
    countryCode?: string | null;
}

export interface EditUserProfileResponse {
    userId?: string | null;
    message?: string | null;
}

export interface ForgotPasswordRequest {
    emailAddress?: string | null;
}

export interface HttpValidationProblemDetails {
    type?: string | null;
    title?: string | null;
    status?: number | null;
    detail?: string | null;
    instance?: string | null;
    errors?: Record<string, string[]>;
}

export interface AccountIsVerifiedResponseDto {
    isVerified: boolean;
}

export type JToken = JToken[];

export interface LinkJoinWebinarRequestDto {
    linkCode: string;
}

export interface AnonymousJoinWebinarRequest {
    webinarId: string;
    anonymousId: string;
    authorizationCode?: string;
}

export interface JoinWebinarRequestDto {
    webinarId: string;
    clientType: number;
    authenticationCode?: string | null;
}

export interface LoginRequest {
    username?: string | null;
    password?: string | null;
    createSession?: boolean | null;
}

export interface TokenResponse {
    isAuthenticated: boolean;
    token: string | null;

    mainAccount: AccountData | null;
    activeAccount: AccountData | null;

    managedAccounts: AccountData[] | null;

    // The active account's rights, by name ("CanBroadcast", ...). Also claims inside token, but
    // /login/v2/me answers the server-side render without a token, and the rights-gated UI has to
    // come out the same on both sides -- so they are read from here, never decoded from the token.
    rights: string[] | null;
}

export interface RegisterRequest {
    firstName?: string | null;
    lastName?: string | null;
    nickname?: string | null;
    companyName?: string | null;
    countryCode?: string | null;
    emailAddress?: string | null;
    password?: string | null;
    captcha?: string | null;
}

export interface ResetPasswordRequest {
    linkId?: string | null;
    password?: string | null;
}

export interface StringStringTuple {
    item1?: string | null;
    item2?: string | null;
}

export interface AccountProfile {
    firstName?: string | null;
    lastName?: string | null;
    nickname: string | null;
    companyName?: string | null;
    countryCode?: string | null;
    emailAddress: string;
}

export enum AccountInviteStatus {
    Pending,

    InviteQueued,
    Invited,

    ReminderQueued,
    Reminded,

    Accepted,
    Rejected,
    Expired,

    Error = 500,

    Blacklisted = 600
}

export interface AccountInvite extends AccountProfile {
    accountInviteId: string;
    status: AccountInviteStatus;
    errorMessage?: string;
    createdAt: string;
    inviteQueuedAt?: string | null;
    inviteAt?: string | null;

    remindedQueuedAt?: string | null;
    remindedAt?: string | null;

    acceptedAt?: string | null;
    rejectedAt?: string | null;

    errorAt?: string | null;
}

export enum EmailListStatus {
    Importing = 0,
    Active = 1,
    Deleted = 2
}

export enum EmailSendStatus {
    Pending = 0,
    Sent = 1,
    Failed = 2,
    Blacklisted = 3,
    AlreadyInterested = 4,
    AlreadySentInAnotherList = 5
}

export enum MarketingCampaignStatus {
    Draft = 0,
    Sent = 1
}

export interface MarketingCampaignSummary {
    id: string;
    name: string;
    createdDate: string;
    status: MarketingCampaignStatus;
    sentCount: number;
    interestedNowCount: number;
}

export interface MarketingCampaignDetail {
    id: string;
    webinarId: string;
    name: string;
    createdDate: string;
    status: MarketingCampaignStatus;
    subject: string;
    body: string;
    excludeInterested: boolean;
    targetListIds: string[];
    sentAt?: string;
    recipientCount: number;
    sentCount: number;
    failedCount: number;
    blacklistedCount: number;
    alreadyInterestedCount: number;
    alreadySentInAnotherListCount: number;
    interestedNowCount: number;
}

export interface CampaignRecipient {
    id: string;
    email: string;
    firstName?: string | null;
    lastName?: string | null;
    companyName?: string | null;
    languageCode?: string | null;
    sourceListId: string;
    status: EmailSendStatus;
    sentAt?: string | null;
    error?: string | null;
}

export interface PagedResult<T> {
    items: T[];
    total: number;
    skip: number;
    take: number;
}

export interface CampaignLists {
    global: EmailListSummary[];
    webinar: EmailListSummary[];
}

export enum BlacklistSource {
    UnsubscribeLink = 0,
    AdminUi = 1,
    ProfileToggle = 2
}

export interface EmailListSummary {
    id: string;
    description: string;
    importedDate: string;
    status: EmailListStatus;
    recipientCount: number;
    sendCount: number;
    lastSendAt?: string;
}

export interface EmailListSend {
    sendId: string;
    templateType: EmailTemplateType;
    webinarId?: string;
    webinarTitle?: string;
    sentByAccountId: string;
    sentAt: string;
    recipientCount: number;
    sentCount: number;
    failedCount: number;
    blacklistedCount: number;
    subTemplate?: string;
    alreadyInterestedCount: number;
}

export interface CampaignUsage {
    campaignId: string;
    webinarId: string;
    name: string;
    usedAt: string;
}

export interface EmailListDetail {
    id: string;
    description: string;
    importedDate: string;
    status: EmailListStatus;
    recipientCount: number;
    sends: EmailListSend[];
    usedByCampaign: boolean;
    campaigns: CampaignUsage[];
}

export interface EmailListRecipient extends AccountProfile {
    id: string;
    sendStatuses: Record<string, EmailSendStatus>;
}

export interface EmailListSendRequest {
    templateType: EmailTemplateType;
    webinarId?: string;
    subject?: string;
    body?: string;
    excludeInterested?: boolean;
}

export interface BlacklistEntry {
    email: string;
    isBlacklisted: boolean;
    blacklistedAt: string;
    source: BlacklistSource;
}

export interface ChatLinkSecuritySettings {
    allowedDomains: string[];
    blockedDomains: string[];
}

export interface EmailPreference {
    unsubscribed: boolean;
}

export interface UnsubscribeResult {
    brandTitle: string;
}

export interface AccountAssets {
    profileImage?: ImageAsset;
    bannerImage?: ImageAsset;
}

export interface AccountData {
    accountId: string;
    isVerified: boolean;
    profile: AccountProfile;
    assets: AccountAssets;
}

export interface ApiKeyInfo {
    keyId: string;
    name: string;
    createdAt: string;
    revokedAt: string | null;
    lastUsedAt: string | null;
    isRevoked: boolean;
}

export interface ApiKeyCreated {
    plaintextKey: string;
    info: ApiKeyInfo;
}

export interface AnswersByParticipant {
    participant: Participant;
    answers: QuestionAnswer[];
}

export interface QuestionAnswer {
    participantId: string;
    questionId: string;
    openAnswer: string;
    selectedAnswers: string[];
    timestamp: string;
    clientType: ClientType;
}

export interface ParticipantExamResult extends Participant {
    result: ExamResultType;
    score: number;
    timestamp?: string;
}

export interface ChatSettings {
    enableOnDemandChatForwarding: boolean;
}

export interface ChatChannelWithParticipant extends ChatChannel {
    participant?: Participant;
}

export interface AccountManagerBaseRights {
    rights: string[];
}

export interface AccountManagerRights extends AccountManagerBaseRights {
    accountId: string;
}

export enum AccountManagerInviteStatus {
    Pending = 0,
    Rejected = 1,
    Deleted = 2
}

export interface AccountManagerInvite extends AccountManagerBaseRights {
    emailAddress: string;
    status: AccountManagerInviteStatus;
}

export interface AccountManager {
    account: AccountData;
    rights: AccountManagerRights;
}

export enum AccountLinkCodeStatus {
    NotFound,
    Invalid,
    IsUsed,
    Expired,
    Valid
}

export interface InviteInformation {
    account: AccountData;
    status: AccountLinkCodeStatus;
    isValid: boolean;
}

export enum VideoPublisherAction {
    Publishing,
    KeyframeExtraction,
    Encoding
}

export enum VideoPublisherStatus {
    Publishing,
    Success,
    Error
}

export enum VideoPublisherStep {
    GetDuration,
    Encode,
    CreateHls,
    KeyframeExtraction,
    Cleanup
}

export interface VideoPublisherState {
    action: VideoPublisherAction;
    status: VideoPublisherStatus;
    step: VideoPublisherStep;
    currentStep: number;
    totalSteps: number;
    progress: number;
    error?: string;
}

export interface DeepgramTranscribeSettings {
    trackId: string;
    isEnabled: boolean;
    isDefault: boolean;
    model: string;
    language: string;
    smartFormat: boolean;
}

export interface WebRtcStatistics {
    timestamp: string;
    videoScore: number;
    videoPacketsSent: number;
    audioScore: number;
    audioPacketsSent: number;
    retransmittedPacketsSent: number;
    packetDelay: number;
    bytesSent: number;
}

export interface UsageRangeSummary {
    fromDay: string;
    toDay: string;
    values: Record<string, number>;
    stockDeltas: Record<string, number>;
    containsApproximateOnDemandDates: boolean;
    daysCovered: number;
    daysMissing: number;
    /** False when no snapshot exists for the day before the range, so every stockDeltas entry was measured from zero rather than from a level. */
    hasStockBaseline: boolean;
}

export interface UsageDayRow {
    day: string;
    metrics: Record<string, number>;
    isProvisional: boolean;
}

export interface BillableWebinarLine {
    webinarId: string;
    title: string;
    startedOn: string;
    durationMinutes: number;
    peakViewers: number;
    blocks: number;
    billableMinutes: number;
}

export interface BlockSizePeriod {
    fromDay: string;
    toDay: string;
    viewerBlockSize: number;
}

export interface MonthlyUsage {
    month: string;
    billableHours: number;
    allowanceHours: number | null;
    overAllowance: boolean;
}

export interface UsageBillingView {
    billableMinutes: number;
    billableHours: number;
    lines: BillableWebinarLine[];
    blockSizes: BlockSizePeriod[];
    contractedHoursPerMonth: number | null;
    lastTwelveMonths: MonthlyUsage[];
    monthsOverAllowance: number;
}

export type QueryParamsType = Record<string | number, any>;
export type ResponseFormat = keyof Omit<Body, 'body' | 'bodyUsed'>;

export interface FullRequestParams extends Omit<RequestInit, 'body'> {
    /** set parameter to `true` for call `securityWorker` for this request */
    secure?: boolean;
    /** request path */
    path: string;
    /** content type of request body */
    type?: ContentType;
    /** query params */
    query?: QueryParamsType;
    /** format of response (i.e. response.json() -> format: "json") */
    format?: ResponseFormat;
    /** request body */
    body?: unknown;
    /** base url */
    baseUrl?: string;
    /** request cancellation token */
    cancelToken?: CancelToken;
}

export type RequestParams = Omit<FullRequestParams, 'body' | 'method' | 'query' | 'path'>;

export interface ApiConfig<SecurityDataType = unknown> {
    baseUrl?: string;
    baseApiParams?: Omit<RequestParams, 'baseUrl' | 'cancelToken' | 'signal'>;
    securityWorker?: (securityData: SecurityDataType | null) => Promise<RequestParams | void> | RequestParams | void;
    customFetch?: typeof fetch;
}

export interface HttpResponse<D extends unknown, E extends unknown = unknown> extends Response {
    data: D;
    error: E;
}

type CancelToken = Symbol | string | number;

export enum ContentType {
    Json = 'application/json',
    FormData = 'multipart/form-data',
    UrlEncoded = 'application/x-www-form-urlencoded'
}

export class HttpClient<SecurityDataType = unknown> {
    public baseUrl: string = 'http://webinar_app';
    private securityData: SecurityDataType | null = null;
    private securityWorker?: ApiConfig<SecurityDataType>['securityWorker'];
    private abortControllers = new Map<CancelToken, AbortController>();
    private customFetch = (...fetchParams: Parameters<typeof fetch>) => fetch(...fetchParams);

    private baseApiParams: RequestParams = {
        credentials: 'same-origin',
        headers: {},
        redirect: 'follow',
        referrerPolicy: 'no-referrer'
    };

    constructor(apiConfig: ApiConfig<SecurityDataType> = {}) {
        Object.assign(this, apiConfig);
    }

    public setSecurityData = (data: SecurityDataType | null) => {
        this.securityData = data;
    };

    private encodeQueryParam(key: string, value: any) {
        const encodedKey = encodeURIComponent(key);
        return `${encodedKey}=${encodeURIComponent(typeof value === 'number' ? value : `${value}`)}`;
    }

    private addQueryParam(query: QueryParamsType, key: string) {
        return this.encodeQueryParam(key, query[key]);
    }

    private addArrayQueryParam(query: QueryParamsType, key: string) {
        const value = query[key];
        return value.map((v: any) => this.encodeQueryParam(key, v)).join('&');
    }

    protected toQueryString(rawQuery?: QueryParamsType): string {
        const query = rawQuery || {};
        const keys = Object.keys(query).filter((key) => 'undefined' !== typeof query[key]);
        return keys.map((key) => (Array.isArray(query[key]) ? this.addArrayQueryParam(query, key) : this.addQueryParam(query, key))).join('&');
    }

    protected addQueryParams(rawQuery?: QueryParamsType): string {
        const queryString = this.toQueryString(rawQuery);
        return queryString ? `?${queryString}` : '';
    }

    private contentFormatters: Record<ContentType, (input: any) => any> = {
        [ContentType.Json]: (input: any) => (input !== null && (typeof input === 'object' || typeof input === 'string') ? JSON.stringify(input) : input),
        [ContentType.FormData]: (input: any) =>
            Object.keys(input || {}).reduce((formData, key) => {
                const property = input[key];
                formData.append(
                    key,
                    property instanceof Blob ? property : typeof property === 'object' && property !== null ? JSON.stringify(property) : `${property}`
                );
                return formData;
            }, new FormData()),
        [ContentType.UrlEncoded]: (input: any) => this.toQueryString(input)
    };

    private mergeRequestParams(params1: RequestParams, params2?: RequestParams): RequestParams {
        return {
            ...this.baseApiParams,
            ...params1,
            ...(params2 || {}),
            headers: {
                ...(this.baseApiParams.headers || {}),
                ...(params1.headers || {}),
                ...((params2 && params2.headers) || {})
            }
        };
    }

    private createAbortSignal = (cancelToken: CancelToken): AbortSignal | undefined => {
        if (this.abortControllers.has(cancelToken)) {
            const abortController = this.abortControllers.get(cancelToken);
            if (abortController) {
                return abortController.signal;
            }
            return void 0;
        }

        const abortController = new AbortController();
        this.abortControllers.set(cancelToken, abortController);
        return abortController.signal;
    };

    public abortRequest = (cancelToken: CancelToken) => {
        const abortController = this.abortControllers.get(cancelToken);

        if (abortController) {
            abortController.abort();
            this.abortControllers.delete(cancelToken);
        }
    };

    public request = async <T = any>(params: FullRequestParams): Promise<HttpResponse<ApiResponse<T>, BaseApiResponse>> => {
        return this.fullRequest<ApiResponse<T>, BaseApiResponse>(params);
    };

    public fullRequest = async <T = any, E = any>({
        body,
        secure,
        path,
        type,
        query,
        format,
        baseUrl,
        cancelToken,
        ...params
    }: FullRequestParams): Promise<HttpResponse<T, E>> => {
        const secureParams =
            ((typeof secure === 'boolean' ? secure : this.baseApiParams.secure) && this.securityWorker && (await this.securityWorker(this.securityData))) || {};
        const requestParams = this.mergeRequestParams(params, secureParams);
        const queryString = query && this.toQueryString(query);
        const payloadFormatter = this.contentFormatters[type || ContentType.Json];
        const responseFormat = format || requestParams.format;

        return this.customFetch(`${baseUrl || this.baseUrl || ''}${path}${queryString ? `?${queryString}` : ''}`, {
            ...requestParams,
            headers: {
                ...(type && type !== ContentType.FormData ? { 'Content-Type': type } : {}),
                ...(requestParams.headers || {})
            },
            signal: cancelToken ? this.createAbortSignal(cancelToken) : void 0,
            body: typeof body === 'undefined' || body === null ? null : payloadFormatter(body),
            credentials: 'include'
        }).then(async (response) => {
            const r = response as HttpResponse<T, E>;
            r.data = null as unknown as T;
            r.error = null as unknown as E;

            const data = !responseFormat
                ? r
                : await response[responseFormat]()
                      .then((data) => {
                          if (r.ok) {
                              r.data = data;
                          } else {
                              r.error = data;
                          }
                          return r;
                      })
                      .catch((e) => {
                          r.error = e;
                          return r;
                      });

            if (cancelToken) {
                this.abortControllers.delete(cancelToken);
            }

            if (!response.ok) throw data;
            return data;
        });
    };
}

export class Api<SecurityDataType extends unknown> extends HttpClient<SecurityDataType> {
    dashboard = {
        getMyStatistics: () =>
            this.request<DashboardStatisticsData>({
                path: `/dashboard/my-statistics`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getContentSummary: (webinarId: string) =>
            this.request<WebinarContentSummary>({
                path: `/dashboard/${webinarId}/content-summary`,
                method: 'GET',
                secure: true,
                format: 'json'
            })
    };
    poll = {
        get: (webinarId: string) =>
            this.request<PollWithPoints[]>({
                path: `/poll/${webinarId}`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        remove: (data: RemovePollRequest) =>
            this.request<boolean>({
                path: `/poll/${data.webinarId}/${data.pollId}`,
                method: 'DELETE',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        edit: (data: EditPollRequest) =>
            this.request<boolean>({
                path: `/poll/${data.webinarId}`,
                method: 'PUT',
                body: data,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        add: (data: AddPollRequest) =>
            this.request<boolean>({
                path: `/poll/${data.webinarId}`,
                method: 'POST',
                body: data,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        getVoteWeights: (data: PollWeightsRequest) =>
            this.request<PollVoteWeights>({
                path: `/poll/${data.webinarId}/${data.pollId}/weights`,
                method: 'GET',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        setDefaultVoteWeight: (data: SetDefaultVoteWeightRequest) =>
            this.request<PollVoteWeights>({
                path: `/poll/${data.webinarId}/${data.pollId}/default-weight`,
                method: 'PUT',
                body: data.weight,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        clearVoteWeights: (data: PollWeightsRequest) =>
            this.request<PollVoteWeights>({
                path: `/poll/${data.webinarId}/${data.pollId}/weights`,
                method: 'DELETE',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        removeVoteWeight: (data: RemoveVoteWeightRequest) =>
            this.request<PollVoteWeights>({
                path: `/poll/${data.webinarId}/${data.pollId}/weights/${data.emailAddress}`,
                method: 'DELETE',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        addVoteWeights: (data: AddPollWeightsRequest) =>
            this.request<PollVoteWeights>({
                path: `/poll/${data.webinarId}/${data.pollId}/weights`,
                method: 'PUT',
                secure: true,
                body: data.weights,
                type: ContentType.Json,
                format: 'json'
            }),

        importVoteWeights: (data: ImportPollWeightsRequest) =>
            this.request<PollVoteWeights>({
                path: `/poll/${data.webinarId}/${data.pollId}/import-weights`,
                method: 'DELETE',
                secure: true,
                body: data.weights,
                type: ContentType.Json,
                format: 'json'
            })
    };
    slideDeck = {
        get: (webinarId: string) =>
            this.request<SlideDeck>({
                path: `/slide-deck/${webinarId}`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        upload: (webinarId: string, data: UploadSlideDeckRequest) =>
            this.request<boolean>({
                path: `/slide-deck/${webinarId}`,
                method: 'POST',
                body: data,
                secure: true,
                type: ContentType.FormData,
                format: 'json'
            }),

        delete: (webinarId: string) =>
            this.request<boolean>({
                path: `/slide-deck/${webinarId}`,
                method: 'DELETE',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            })
    };
    chapter = {
        update: (data: { webinarId: string; data: Chapter[] }) =>
            this.request<boolean>({
                path: `/chapter/${data.webinarId}`,
                method: 'POST',
                body: data.data,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        get: (webinarId: string) =>
            this.request<Chapter[]>({
                path: `/chapter/${webinarId}`,
                method: 'GET',
                secure: true,
                format: 'json'
            })
    };
    chat = {
        getChatSettings: (webinarId: string) =>
            this.request<ChatSettings>({
                path: `/chat/${webinarId}/settings`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        setChatSettings: (data: { webinarId: string; settings: ChatSettings }) =>
            this.request<ChatSettings>({
                path: `/chat/${data.webinarId}/settings`,
                method: 'POST',
                body: data.settings,
                secure: true,
                format: 'json',
                type: ContentType.Json
            })
    };
    interest = {
        getStatus: (webinarId: string) =>
            this.request<WebinarDataWithStatus>({
                path: `/interest/${webinarId}/status`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        showInterest: (webinarId: string) =>
            this.request<boolean>({
                path: `/interest/${webinarId}`,
                method: 'PUT',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        retractInterest: (webinarId: string) =>
            this.request<boolean>({
                path: `/interest/${webinarId}`,
                method: 'DELETE',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            })
    };
    webinars = {
        getUpcoming: () =>
            this.request<WebinarDataWithStatus[]>({
                path: `/webinars/upcoming`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getOnDemand: () =>
            this.request<WebinarDataWithStatus[]>({
                path: `/webinars/on-demand`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getMyUpcoming: () =>
            this.request<WebinarContentSummary[]>({
                path: `/webinars/my-upcoming`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getMyEnded: () =>
            this.request<WebinarContentSummary[]>({
                path: `/webinars/my-ended`,
                method: 'GET',
                secure: true,
                format: 'json'
            })
    };
    webinarSecurity = {
        checkIsAllowed: (data: { webinarId: string; emailAddresses: string[] }) =>
            this.request<IsAllowedResponse[]>({
                path: `/webinar-security/${data.webinarId}/check-is-allowed`,
                method: 'POST',
                secure: true,
                format: 'json',
                body: data.emailAddresses,
                type: ContentType.Json
            }),

        getSettings: (webinarId: string) =>
            this.request<WebinarSecuritySettings>({
                path: `/webinar-security/${webinarId}`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        updateSettings: (data: { webinarId: string; settings: WebinarSecuritySettings }) =>
            this.request<boolean>({
                path: `/webinar-security/${data.webinarId}`,
                method: 'POST',
                secure: true,
                format: 'json',
                body: data.settings,
                type: ContentType.Json
            })
    };
    survey = {
        get: (webinarId: string) =>
            this.request<SurveyWithPoints>({
                path: `/survey/${webinarId}`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        update: (data: { webinarId: string; survey: SurveyWithPoints }) =>
            this.request<boolean>({
                path: `/survey/${data.webinarId}`,
                method: 'PUT',
                body: data.survey,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            })
    };
    exam = {
        get: (webinarId: string) =>
            this.request<ExamWithPoints>({
                path: `/exam/${webinarId}`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        update: (data: { webinarId: string; exam: ExamWithPoints }) =>
            this.request<boolean>({
                path: `/exam/${data.webinarId}`,
                method: 'PUT',
                body: data.exam,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            })
    };
    accountInvite = {
        get: (accountInviteId: string) =>
            this.request<AccountProfile>({
                path: `/account-invite/${accountInviteId}`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),
        accept: (data: { register: RegisterRequest; accountInviteId: string }) =>
            this.request<TokenResponse>({
                path: `/account-invite/v2/${data.accountInviteId}`,
                method: 'POST',
                secure: true,
                body: data.register,
                type: ContentType.Json,
                format: 'json'
            }),
        reject: (accountInviteId: string) =>
            this.request<boolean>({
                path: `/account-invite/${accountInviteId}`,
                method: 'DELETE',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            })
    };
    accountInvites = {
        get: () =>
            this.request<AccountInvite[]>({
                path: `/account-invites`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),
        invite: (accounts: AccountProfile[]) =>
            this.request<boolean>({
                path: `/account-invites`,
                method: 'POST',
                secure: true,
                body: accounts,
                type: ContentType.Json,
                format: 'json'
            }),
        remove: (emailAddresses: string[]) =>
            this.request<boolean>({
                path: `/account-invites`,
                method: 'DELETE',
                body: emailAddresses,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),
        reset: (emailAddresses: string[]) =>
            this.request<boolean>({
                path: `/account-invites`,
                method: 'PATCH',
                body: emailAddresses,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            })
    };
    invites = {
        get: (webinarId: string) =>
            this.request<InvitedParticipant[]>({
                path: `/v2/invites/${webinarId}`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),
        invite: (data: { webinarId: string; participants: AccountProfile[] }) =>
            this.request<boolean>({
                path: `/invites/${data.webinarId}`,
                method: 'POST',
                secure: true,
                body: data.participants,
                type: ContentType.Json,
                format: 'json'
            }),
        remove: (data: { webinarId: string; emailAddress: string }) =>
            this.request<boolean>({
                path: `/invites/${data.webinarId}`,
                method: 'DELETE',
                body: data.emailAddress,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),
        email: (data: { webinarId: string; template?: string; emailAddresses: string[] }) =>
            this.request<boolean>({
                path: `/invites/${data.webinarId}/email`,
                method: 'POST',
                secure: true,
                body: {
                    template: data.template,
                    emailAddresses: data.emailAddresses
                },
                type: ContentType.Json,
                format: 'json'
            }),
        emailReminder: (data: { webinarId: string; template?: string; emailAddresses: string[] }) =>
            this.request<boolean>({
                path: `/invites/${data.webinarId}/email-reminder`,
                method: 'POST',
                secure: true,
                body: {
                    template: data.template,
                    emailAddresses: data.emailAddresses
                },
                type: ContentType.Json,
                format: 'json'
            }),
        getSettings: (webinarId: string) =>
            this.request<WebinarInviteSettings>({
                path: `/invites/${webinarId}/settings`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),
        updateSettings: (data: { webinarId: string; settings: WebinarInviteSettings }) =>
            this.request<boolean>({
                path: `/invites/${data.webinarId}/settings`,
                method: 'PUT',
                secure: true,
                body: data.settings,
                type: ContentType.Json,
                format: 'json'
            })
    };
    insights = {
        getInterestedAccounts: (webinarId: string) =>
            this.request<InterestedAccount[]>({
                path: `/insights/${webinarId}/interested-accounts`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getParticipants: (webinarId: string) =>
            this.request<Participant[]>({
                path: `/insights/${webinarId}/participants`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getLiveParticipants: (webinarId: string) =>
            this.request<Participant[]>({
                path: `/insights/${webinarId}/live/participants`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getOnDemandParticipants: (webinarId: string) =>
            this.request<Participant[]>({
                path: `/insights/${webinarId}/on-demand/participants`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getLivePollResults: (webinarId: string) =>
            this.request<PollWithVotes[]>({
                path: `/insights/${webinarId}/live/poll-results`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getOnDemandPollResults: (webinarId: string) =>
            this.request<PollWithVotes[]>({
                path: `/insights/${webinarId}/on-demand/poll-results`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getLiveSurveyAnswers: (webinarId: string) =>
            this.request<AnswersByParticipant[]>({
                path: `/insights/${webinarId}/live/survey-answers`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getOnDemandSurveyAnswers: (webinarId: string) =>
            this.request<AnswersByParticipant[]>({
                path: `/insights/${webinarId}/on-demand/survey-answers`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getExamAnswers: (webinarId: string) =>
            this.request<AnswersByParticipant[]>({
                path: `/insights/${webinarId}/exam-answers`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getExamResults: (webinarId: string) =>
            this.request<ParticipantExamResult[]>({
                path: `/insights/${webinarId}/exam-results`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getLiveExamAnswers: (webinarId: string) =>
            this.request<AnswersByParticipant[]>({
                path: `/insights/${webinarId}/live/exam-answers`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getOnDemandExamAnswers: (webinarId: string) =>
            this.request<AnswersByParticipant[]>({
                path: `/insights/${webinarId}/on-demand/exam-answers`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getChatMessagesCount: (webinarId: string) =>
            this.request<number>({
                path: `/insights/${webinarId}/chat-messages-count`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getChatMessages: (webinarId: string) =>
            this.request<ChatChannelWithParticipant[]>({
                path: `/insights/${webinarId}/chat-messages`,
                method: 'GET',
                secure: true,
                format: 'json'
            })
    };
    analytics = {
        getWebRtcStatistics: (webinarId: string) =>
            this.request<WebRtcStatistics[]>({
                path: `/analytics/${webinarId}/webrtc-statistics`,
                method: 'GET',
                secure: true,
                format: 'json'
            })
    };
    webinar = {
        get: (webinarId: string) =>
            this.request<WebinarDataWithAssets>({
                path: `/webinar/${webinarId}`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getStatus: (webinarId: string) =>
            this.request<WebinarStatus>({
                path: `/webinar/${webinarId}/status`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getTags: () =>
            this.request<string[]>({
                path: `/webinar/get-tags`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        plan: (data: EditWebinarData) =>
            this.request<EditWebinarData>({
                path: `/webinar/plan`,
                method: 'POST',
                body: data,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        planTest: (data: EditWebinarData) =>
            this.request<WebinarDataWithAssets>({
                path: `/webinar/plan-test`,
                method: 'POST',
                body: data,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        updateProfileImageAltText: (data: { webinarId: string; altText: string }) =>
            this.request<boolean>({
                path: `/webinar/${data.webinarId}/profile-image/alt-text`,
                method: 'PUT',
                body: { altText: data.altText },
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        deleteProfileImage: (webinarId: string) =>
            this.request<boolean>({
                path: `/webinar/${webinarId}/profile-image`,
                method: 'DELETE',
                secure: true,
                format: 'json'
            }),

        deleteBannerImage: (webinarId: string) =>
            this.request<boolean>({
                path: `/webinar/${webinarId}/banner-image`,
                method: 'DELETE',
                secure: true,
                format: 'json'
            }),

        getEdit: (webinarId: string) =>
            this.request<EditWebinarData>({
                path: `/webinar/${webinarId}/edit`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        edit: (data: EditWebinarData) =>
            this.request<EditWebinarData>({
                path: `/webinar/${data.webinarId}/edit`,
                method: 'PUT',
                body: data,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        cancel: (webinarId: string) =>
            this.request<boolean>({
                path: `/webinar/${webinarId}/cancel`,
                method: 'PUT',
                secure: true,
                format: 'json'
            }),

        toggleIsPublished: (webinarId: string) =>
            this.request<boolean>({
                path: `/webinar/${webinarId}/toggle-is-published`,
                method: 'PATCH',
                secure: true,
                format: 'json'
            }),

        setWebinarVisibility: (data: { webinarId: string; visibility: WebinarVisibility }) =>
            this.request<boolean>({
                path: `/webinar/${data.webinarId}/visibility`,
                method: 'PATCH',
                body: data.visibility,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        setOnDemandEnabled: (data: { webinarId: string; enabled: boolean }) =>
            this.request<boolean>({
                path: `/webinar/${data.webinarId}/on-demand-enabled`,
                method: 'PATCH',
                body: data.enabled,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        getModeratorCode: (webinarId: string) =>
            this.request<string>({
                path: `/webinar/${webinarId}/moderator-code`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getBroadcasterCode: (webinarId: string) =>
            this.request<string>({
                path: `/webinar/${webinarId}/broadcaster-code`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getSpeakerCode: (webinarId: string) =>
            this.request<string>({
                path: `/webinar/${webinarId}/speaker-code`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getDeleteCode: (webinarId: string) =>
            this.request<string>({
                path: `/webinar/${webinarId}/delete-code`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        delete: (data: { webinarId: string; deleteCode: string }) =>
            this.request<boolean>({
                path: `/webinar/${data.webinarId}`,
                method: 'DELETE',
                body: data.deleteCode,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            })
    };
    followUp = {
        get: (webinarId: string) =>
            this.request<FollowUpStatistics[]>({
                path: `/follow-up/${webinarId}`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getById: (data: { webinarId: string; followUpId: string }) =>
            this.request<FollowUpWithEmailTemplate>({
                path: `/follow-up/${data.webinarId}/${data.followUpId}`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getProcessedById: (data: { webinarId: string; followUpId: string }) =>
            this.request<ProcessedFollowUpParticipantData[]>({
                path: `/follow-up/${data.webinarId}/${data.followUpId}/processed`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        addOrUpdate: (data: { webinarId: string; followUp: FollowUpWithEmailTemplate }) =>
            this.request<FollowUpWithEmailTemplate>({
                path: `/follow-up/${data.webinarId}`,
                method: 'POST',
                secure: true,
                format: 'json',
                body: data.followUp,
                type: ContentType.Json
            }),

        remove: (data: { webinarId: string; followUpId: string }) =>
            this.request<boolean>({
                path: `/follow-up/${data.webinarId}/${data.followUpId}`,
                method: 'DELETE',
                secure: true,
                format: 'json',
                type: ContentType.Json
            })
    };
    webinarAttachments = {
        get: (webinarId: string) =>
            this.request<WebinarAttachment[]>({
                path: `/webinar-attachments/${webinarId}`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),
        edit: (data: { webinarId: string; attachment: WebinarAttachment }) =>
            this.request<boolean>({
                path: `/webinar-attachments/${data.webinarId}`,
                method: 'PATCH',
                secure: true,
                format: 'json',
                body: data.attachment,
                type: ContentType.Json
            }),
        remove: (data: { webinarId: string; attachmentId: string }) =>
            this.request<boolean>({
                path: `/webinar-attachments/${data.webinarId}/${data.attachmentId}`,
                method: 'DELETE',
                secure: true,
                format: 'json'
            })
    };
    testSuite = {
        getTestChannel: () =>
            this.request<VindralChannelInformation>({
                path: `/test-suite`,
                method: 'GET',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),
        lineProbeSession: () =>
            this.request<SessionGrant>({
                path: `/test-suite/line-probe/session`,
                method: 'GET',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),
        lineProbeStats: (batch: LineProbeStatsBatch) =>
            this.request<boolean>({
                path: `/test-suite/line-probe/stats`,
                method: 'POST',
                body: batch,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            })
    };
    tier = {
        getAvailable: () =>
            this.request<Tier[]>({
                path: `/tier`,
                method: 'GET',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        get: (tierId: string) =>
            this.request<Tier>({
                path: `/tier/${tierId}`,
                method: 'GET',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        isAvailable: (data: EditWebinarData) =>
            this.request<boolean>({
                path: `/tier/is-available`,
                method: 'POST',
                body: data,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            })
    };
    anonymous = {
        get: ({ webinarId, authorizationCode }: { webinarId: string; authorizationCode?: string }) =>
            this.request<WebinarDataWithStatus>({
                path: `/anonymous/${webinarId}${authorizationCode ? `?authorizationCode=${authorizationCode}` : ''}`,
                method: 'GET',
                format: 'json'
            }),

        joinLiveWebinar: (data: AnonymousJoinWebinarRequest) =>
            this.request<string>({
                path: `/anonymous/join-live-webinar`,
                method: 'POST',
                body: data,
                type: ContentType.Json,
                format: 'json'
            }),
        joinOnDemandWebinar: (data: AnonymousJoinWebinarRequest) =>
            this.request<string>({
                path: `/anonymous/join-on-demand-webinar`,
                method: 'POST',
                body: data,
                type: ContentType.Json,
                format: 'json'
            })
    };
    watchLink = {
        getWebinar: (data: LinkJoinWebinarRequestDto) =>
            this.request<WebinarDataWatchLink>({
                path: `/watch-link/get-webinar`,
                method: 'POST',
                body: data,
                type: ContentType.Json,
                format: 'json'
            }),
        joinLiveWebinar: (data: LinkJoinWebinarRequestDto) =>
            this.request<string>({
                path: `/watch-link/join-live-webinar`,
                method: 'POST',
                body: data,
                type: ContentType.Json,
                format: 'json'
            }),
        joinOnDemandWebinar: (data: LinkJoinWebinarRequestDto) =>
            this.request<string>({
                path: `/watch-link/join-on-demand-webinar`,
                method: 'POST',
                body: data,
                type: ContentType.Json,
                format: 'json'
            })
    };
    liveWebinar = {
        joinWebinar: (data: JoinWebinarRequestDto) =>
            this.request<string>({
                path: `/live-webinar/join-webinar`,
                method: 'POST',
                body: data,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            })
    };
    videoEditor = {
        joinWebinar: (data: JoinWebinarRequestDto) =>
            this.request<string>({
                path: `/video-editor/join-webinar`,
                method: 'POST',
                body: data,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),
        publishWebinar: (data: { webinarId: string; events: any[] }) =>
            this.request<boolean>({
                path: `/video-editor/${data.webinarId}/publish`,
                method: 'POST',
                body: data.events,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),
        getStatus: (webinarId: string) =>
            this.request<VideoPublisherState>({
                path: `/video-editor/${webinarId}`,
                method: 'GET',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),
        getKeyframes: (webinarId: string) =>
            this.request<string[]>({
                path: `/video-editor/${webinarId}/keyframes`,
                method: 'GET',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            })
    };
    onDemandWebinar = {
        joinWebinar: (data: JoinWebinarRequestDto) =>
            this.request<string>({
                path: `/on-demand-webinar/join-webinar`,
                method: 'POST',
                body: data,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),
        loadJson: (url: string) =>
            this.fullRequest<any, HttpValidationProblemDetails>({
                path: url.replace('https://', '').replace('http://', ''),
                baseUrl: url.indexOf('https://') !== -1 ? 'https://' : 'http://',
                method: 'GET',
                type: ContentType.Json,
                format: 'json'
            })
    };
    liveTranscribe = {
        getDeepgramSettings: (webinarId: string) =>
            this.request<DeepgramTranscribeSettings>({
                path: `/live-transcribe/${webinarId}/deepgram`,
                method: 'GET',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        setDeepgramSettings: (data: { webinarId: string; settings: DeepgramTranscribeSettings }) =>
            this.request<boolean>({
                path: `/live-transcribe/${data.webinarId}/deepgram`,
                method: 'POST',
                secure: true,
                type: ContentType.Json,
                format: 'json',
                body: data.settings
            })
    };
    subtitles = {
        getTrackIds: (webinarId: string) =>
            this.request<string[]>({
                path: `/subtitles/tracks/${webinarId}/ids`,
                method: 'GET',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        getTracks: (webinarId: string) =>
            this.request<SubtitlesTrack[]>({
                path: `/subtitles/tracks/${webinarId}`,
                method: 'GET',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        getTrack: (data: { webinarId: string; trackId: string }) =>
            this.request<SubtitlesTrack>({
                path: `/subtitles/track/${data.webinarId}/${data.trackId}`,
                method: 'GET',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        addOrUpdateTrack: (data: { webinarId: string; track: SubtitlesTrack }) =>
            this.request<SubtitlesTrack>({
                path: `/subtitles/track/${data.webinarId}`,
                method: 'POST',
                secure: true,
                type: ContentType.Json,
                format: 'json',
                body: data.track
            }),

        deleteTrack: (data: { webinarId: string; trackId: string }) =>
            this.request<boolean>({
                path: `/subtitles/track/${data.webinarId}/${data.trackId}`,
                method: 'DELETE',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            })
    };
    subtitlesGenerator = {
        getStatus: (webinarId: string) =>
            this.request<SubtitlesGeneratorStatus>({
                path: `/subtitles-generator/${webinarId}/status`,
                method: 'GET',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        getOptions: (webinarId: string) =>
            this.request<SubtitlesGeneratorOptions>({
                path: `/subtitles-generator/${webinarId}`,
                method: 'GET',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        generateSubtitles: ({ webinarId, options }: { webinarId: string; options: SubtitlesGeneratorOptions }) =>
            this.request<boolean>({
                path: `/subtitles-generator/${webinarId}`,
                method: 'POST',
                body: options,
                secure: true,
                type: ContentType.Json,
                format: 'json'
            })
    };
    login = {
        register: (data: RegisterRequest) =>
            this.request<TokenResponse>({
                path: `/login/v2/register`,
                method: 'POST',
                body: data,
                type: ContentType.Json,
                format: 'json'
            }),

        exists: (email: string) =>
            this.request<boolean>({
                path: `/login/exists`,
                method: 'POST',
                body: email,
                type: ContentType.Json,
                format: 'json'
            }),

        login: (data: LoginRequest) =>
            this.request<TokenResponse>({
                path: `/login/v2`,
                method: 'POST',
                body: data,
                type: ContentType.Json,
                format: 'json'
            }),

        logout: () =>
            this.request<boolean>({
                path: `/login/v2`,
                method: 'DELETE',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        refreshToken: () =>
            this.request<TokenResponse>({
                path: `/login/v2/refresh`,
                method: 'GET',
                type: ContentType.Json,
                format: 'json',
                secure: true
            }),

        me: () =>
            this.request<TokenResponse>({
                path: `/login/v2/me`,
                method: 'GET',
                type: ContentType.Json,
                format: 'json',
                secure: true
            }),

        forgotPassword: (data: ForgotPasswordRequest) =>
            this.request<boolean>({
                path: `/login/forgot-password`,
                method: 'POST',
                body: data,
                type: ContentType.Json,
                format: 'json'
            }),

        resetPassword: (data: ResetPasswordRequest) =>
            this.request<TokenResponse>({
                path: `/login/v2/reset-password/${data.linkId}`,
                method: 'PUT',
                body: { password: data.password },
                type: ContentType.Json,
                format: 'json'
            }),

        changePassword: (data: ChangePasswordRequestDto) =>
            this.request<boolean>({
                path: `/login/change-password`,
                method: 'PUT',
                body: data,
                type: ContentType.Json,
                format: 'json',
                secure: true
            })
    };
    accountManagement = {
        getAccountManagers: () =>
            this.request<AccountManager[]>({
                path: `/account-management/managers`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        updateAccountManager: (manager: AccountManagerRights) =>
            this.request<boolean>({
                path: `/account-management/managers`,
                method: 'PATCH',
                secure: true,
                body: manager,
                type: ContentType.Json,
                format: 'json'
            }),

        removeAccountManager: (managerId: string) =>
            this.request<boolean>({
                path: `/account-management/managers`,
                method: 'DELETE',
                secure: true,
                body: managerId,
                type: ContentType.Json,
                format: 'json'
            }),

        getAccountManagerInvites: () =>
            this.request<AccountManagerInvite[]>({
                path: `/account-management/invites`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        inviteAccountManager: (data: AccountManagerInvite) =>
            this.request<boolean>({
                path: `/account-management/invites`,
                method: 'POST',
                secure: true,
                body: data,
                type: ContentType.Json,
                format: 'json'
            }),

        removeAccountManagerInvite: (emailAddress: string) =>
            this.request<boolean>({
                path: `/account-management/invites`,
                method: 'DELETE',
                secure: true,
                body: emailAddress,
                type: ContentType.Json,
                format: 'json'
            }),

        switchAccount: (accountId: string) =>
            this.request<TokenResponse>({
                path: `/account-management/v2/switch-account`,
                method: 'POST',
                secure: true,
                body: accountId,
                type: ContentType.Json,
                format: 'json'
            }),

        getManagedAccounts: () =>
            this.request<AccountData[]>({
                path: `/account-management/managed`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        getInviteInformation: (inviteCode: string) =>
            this.request<InviteInformation>({
                path: `/account-management/invite/${inviteCode}`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),

        acceptInvite: (inviteCode: string) =>
            this.request<boolean>({
                path: `/account-management/invite/${inviteCode}`,
                method: 'PUT',
                secure: true,
                format: 'json'
            }),

        rejectInvite: (inviteCode: string) =>
            this.request<boolean>({
                path: `/account-management/invite/${inviteCode}`,
                method: 'DELETE',
                secure: true,
                format: 'json'
            })
    };
    emailStyle = {
        get: () =>
            this.request<EmailStyle>({
                path: `/email-style`,
                method: 'GET',
                format: 'json',
                secure: true
            }),

        set: (data: EmailStyle) =>
            this.request<boolean>({
                path: `/email-style`,
                method: 'POST',
                secure: true,
                body: data,
                type: ContentType.Json,
                format: 'json'
            }),

        uploadHeaderImage: (data: UploadEmailHeaderImageRequest) =>
            this.request<string>({
                path: `/email-style/header-image`,
                method: 'POST',
                body: data,
                secure: true,
                type: ContentType.FormData,
                format: 'json'
            }),

        deleteHeaderImage: () =>
            this.request<boolean>({
                path: `/email-style/header-image`,
                method: 'DELETE',
                secure: true,
                format: 'json'
            })
    };
    emailTemplate = {
        get: (data: { templateType: EmailTemplateType; language: string; subTemplate?: string }) =>
            this.request<EmailTemplate>({
                path: `/email-template/${data.templateType}/${data.language}/${data.subTemplate || ''}`,
                method: 'GET',
                format: 'json',
                secure: true
            }),

        set: (data: { templateType: EmailTemplateType; language: string; subTemplate?: string; subject: string; body: string }) =>
            this.request<boolean>({
                path: `/email-template/${data.templateType}/${data.language}/${data.subTemplate || ''}`,
                method: 'POST',
                secure: true,
                body: { subject: data.subject, body: data.body },
                type: ContentType.Json,
                format: 'json'
            }),

        preview: (data: { templateType: EmailTemplateType; subject: string; body: string }) =>
            this.request<EmailTemplate>({
                path: `/email-template/${data.templateType}`,
                method: 'PUT',
                secure: true,
                body: { subject: data.subject, body: data.body },
                type: ContentType.Json,
                format: 'json'
            }),

        getCalendarAttachment: (data: { templateType: EmailTemplateType; subTemplate?: string }) =>
            this.request<boolean>({
                path: `/email-template/${data.templateType}/calendar-attachment/${data.subTemplate || ''}`,
                method: 'GET',
                format: 'json',
                secure: true
            }),

        setCalendarAttachment: (data: { templateType: EmailTemplateType; subTemplate?: string; include: boolean }) =>
            this.request<boolean>({
                path: `/email-template/${data.templateType}/calendar-attachment/${data.subTemplate || ''}`,
                method: 'PUT',
                secure: true,
                body: data.include,
                type: ContentType.Json,
                format: 'json'
            })
    };
    translation = {
        get: (language: string) =>
            this.request<TranslationPatch>({
                path: `/translation/${language}`,
                method: 'GET',
                format: 'json',
                secure: true
            }),

        update: (data: { language: string; patch: { [index: string]: string } }) =>
            this.request<TranslationPatch>({
                path: `/translation/${data.language}`,
                method: 'POST',
                secure: true,
                body: data.patch,
                type: ContentType.Json,
                format: 'json'
            })
    };
    apiKeys = {
        getAll: () =>
            this.request<ApiKeyInfo[]>({
                path: `/account/api-keys`,
                method: 'GET',
                format: 'json',
                secure: true
            }),

        create: (name: string) =>
            this.request<ApiKeyCreated>({
                path: `/account/api-keys`,
                method: 'POST',
                body: { name },
                type: ContentType.Json,
                format: 'json',
                secure: true
            }),

        revoke: (keyId: string) =>
            this.request<boolean>({
                path: `/account/api-keys/${keyId}`,
                method: 'DELETE',
                secure: true,
                format: 'json'
            })
    };

    account = {
        isVerified: () =>
            this.request<boolean>({
                path: `/account/is-verified`,
                method: 'GET',
                format: 'json',
                secure: true
            }),

        verify: (verificationCode: string) =>
            this.request<boolean>({
                path: `/account/verify/${verificationCode}`,
                method: 'PUT',
                format: 'json'
            }),

        resendVerificationEmail: () =>
            this.request<boolean>({
                path: `/account/resend-verification-email`,
                method: 'PUT',
                secure: true,
                format: 'json'
            }),

        get: () =>
            this.request<AccountData>({
                path: `/account`,
                method: 'GET',
                format: 'json',
                secure: true
            }),

        updateProfile: (data: AccountProfile) =>
            this.request<boolean>({
                path: `/account/update-profile`,
                method: 'PUT',
                body: data,
                type: ContentType.Json,
                format: 'json',
                secure: true
            }),

        updateProfileImageAltText: (altText: string) =>
            this.request<boolean>({
                path: `/account/profile-image/alt-text`,
                method: 'PUT',
                body: { altText },
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),

        deleteProfileImage: () =>
            this.request<boolean>({
                path: `/account/profile-image`,
                method: 'DELETE',
                secure: true,
                format: 'json'
            }),

        deleteBannerImage: () =>
            this.request<boolean>({
                path: `/account/banner-image`,
                method: 'DELETE',
                secure: true,
                format: 'json'
            }),

        deleteAccount: (password: string) =>
            this.request<boolean>({
                path: `/account`,
                method: 'DELETE',
                body: password,
                type: ContentType.Json,
                format: 'json',
                secure: true
            })
    };

    emailLists = {
        get: () =>
            this.request<EmailListSummary[]>({
                path: `/email-lists`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),
        getDetail: (id: string) =>
            this.request<EmailListDetail>({
                path: `/email-lists/${id}`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),
        getRecipients: (id: string) =>
            this.request<EmailListRecipient[]>({
                path: `/email-lists/${id}/recipients`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),
        create: (data: { description: string; webinarId?: string }) =>
            this.request<string>({
                path: `/email-lists`,
                method: 'POST',
                secure: true,
                body: { description: data.description, webinarId: data.webinarId },
                type: ContentType.Json,
                format: 'json'
            }),
        addRecipients: (data: { id: string; recipients: AccountProfile[] }) =>
            this.request<boolean>({
                path: `/email-lists/${data.id}/recipients`,
                method: 'POST',
                secure: true,
                body: data.recipients,
                type: ContentType.Json,
                format: 'json'
            }),
        finalize: (id: string) =>
            this.request<boolean>({
                path: `/email-lists/${id}/finalize`,
                method: 'POST',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),
        send: (data: { id: string; request: EmailListSendRequest }) =>
            this.request<string>({
                path: `/email-lists/${data.id}/send`,
                method: 'POST',
                secure: true,
                body: data.request,
                type: ContentType.Json,
                format: 'json'
            }),
        removeRecipient: (data: { id: string; email: string }) =>
            this.request<boolean>({
                path: `/email-lists/${data.id}/recipients`,
                method: 'DELETE',
                secure: true,
                query: { email: data.email },
                format: 'json'
            }),
        updateDescription: (data: { id: string; description: string }) =>
            this.request<boolean>({
                path: `/email-lists/${data.id}`,
                method: 'PUT',
                secure: true,
                body: { description: data.description },
                type: ContentType.Json,
                format: 'json'
            }),
        getDefaultTemplate: (templateType: EmailTemplateType) =>
            this.request<EmailTemplate>({
                path: `/email-lists/template-default/${templateType}`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),
        previewTemplate: (data: { templateType: EmailTemplateType; subject: string; body: string }) =>
            this.request<EmailTemplate>({
                path: `/email-lists/template-preview/${data.templateType}`,
                method: 'PUT',
                secure: true,
                body: { subject: data.subject, body: data.body },
                type: ContentType.Json,
                format: 'json'
            }),
        getSendTemplate: (data: { id: string; sendId: string }) =>
            this.request<EmailTemplate>({
                path: `/email-lists/${data.id}/send-template/${data.sendId}`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),
        getInterestedEmails: (webinarId: string) =>
            this.request<string[]>({
                path: `/email-lists/interested/${webinarId}`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),
        remove: (id: string) =>
            this.request<boolean>({
                path: `/email-lists/${id}`,
                method: 'DELETE',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            })
    };

    marketingCampaigns = {
        get: (webinarId: string) =>
            this.request<MarketingCampaignSummary[]>({
                path: `/webinars/${webinarId}/campaigns`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),
        getDetail: (data: { webinarId: string; id: string }) =>
            this.request<MarketingCampaignDetail>({
                path: `/webinars/${data.webinarId}/campaigns/${data.id}`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),
        getLists: (webinarId: string) =>
            this.request<CampaignLists>({
                path: `/webinars/${webinarId}/campaigns/lists`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),
        getRecipients: (data: { webinarId: string; id: string; skip: number; take: number; search?: string; status?: EmailSendStatus }) =>
            this.request<PagedResult<CampaignRecipient>>({
                path: `/webinars/${data.webinarId}/campaigns/${data.id}/recipients`,
                method: 'GET',
                secure: true,
                query: { skip: data.skip, take: data.take, search: data.search, status: data.status },
                format: 'json'
            }),
        create: (data: { webinarId: string; name: string; subject: string; body: string; excludeInterested: boolean }) =>
            this.request<string>({
                path: `/webinars/${data.webinarId}/campaigns`,
                method: 'POST',
                secure: true,
                body: { name: data.name, subject: data.subject, body: data.body, excludeInterested: data.excludeInterested },
                type: ContentType.Json,
                format: 'json'
            }),
        update: (data: { webinarId: string; id: string; name: string; subject: string; body: string; excludeInterested: boolean; targetListIds: string[] }) =>
            this.request<boolean>({
                path: `/webinars/${data.webinarId}/campaigns/${data.id}`,
                method: 'PUT',
                secure: true,
                body: { name: data.name, subject: data.subject, body: data.body, excludeInterested: data.excludeInterested, targetListIds: data.targetListIds },
                type: ContentType.Json,
                format: 'json'
            }),
        send: (data: { webinarId: string; id: string }) =>
            this.request<boolean>({
                path: `/webinars/${data.webinarId}/campaigns/${data.id}/send`,
                method: 'POST',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            }),
        sendMore: (data: { webinarId: string; id: string; listIds: string[] }) =>
            this.request<boolean>({
                path: `/webinars/${data.webinarId}/campaigns/${data.id}/send-more`,
                method: 'POST',
                secure: true,
                body: { listIds: data.listIds },
                type: ContentType.Json,
                format: 'json'
            }),
        remove: (data: { webinarId: string; id: string }) =>
            this.request<boolean>({
                path: `/webinars/${data.webinarId}/campaigns/${data.id}`,
                method: 'DELETE',
                secure: true,
                type: ContentType.Json,
                format: 'json'
            })
    };

    blacklist = {
        search: (query?: string) =>
            this.request<BlacklistEntry[]>({
                path: `/blacklist`,
                method: 'GET',
                secure: true,
                query: { query },
                format: 'json'
            }),
        remove: (email: string) =>
            this.request<boolean>({
                path: `/blacklist`,
                method: 'DELETE',
                secure: true,
                body: email,
                type: ContentType.Json,
                format: 'json'
            })
    };

    usageStatistics = {
        range: (from: string, to: string) =>
            this.request<UsageRangeSummary>({
                path: `/usage-statistics/range`,
                method: 'GET',
                secure: true,
                query: { from, to },
                format: 'json'
            }),
        daily: (from: string, to: string) =>
            this.request<UsageDayRow[]>({
                path: `/usage-statistics/daily`,
                method: 'GET',
                secure: true,
                query: { from, to },
                format: 'json'
            }),
        billing: (from: string, to: string) =>
            this.request<UsageBillingView>({
                path: `/usage-statistics/billing`,
                method: 'GET',
                secure: true,
                query: { from, to },
                format: 'json'
            }),
        today: () =>
            this.request<UsageDayRow>({
                path: `/usage-statistics/today`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),
        /**
         * Returns the workbook itself rather than the ApiResponse envelope, so it is read as a
         * blob. It goes through the client like every other call because the endpoint is
         * [Authorize]d and the bearer token is attached by the securityWorker — a plain link
         * navigation carries no Authorization header and would 401.
         */
        export: (from: string, to: string) =>
            this.fullRequest<Blob, BaseApiResponse>({
                path: `/usage-statistics/export`,
                method: 'GET',
                secure: true,
                query: { from, to },
                format: 'blob'
            })
    };

    chatLinkSecurity = {
        get: () =>
            this.request<ChatLinkSecuritySettings>({
                path: `/chat-link-security`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),
        update: (data: ChatLinkSecuritySettings) =>
            this.request<ChatLinkSecuritySettings>({
                path: `/chat-link-security`,
                method: 'POST',
                secure: true,
                body: data,
                type: ContentType.Json,
                format: 'json'
            })
    };

    unsubscribe = {
        unsubscribe: (token: string) =>
            this.request<UnsubscribeResult>({
                path: `/unsubscribe`,
                method: 'POST',
                secure: false,
                body: { token },
                type: ContentType.Json,
                format: 'json'
            }),
        resubscribe: (token: string) =>
            this.request<UnsubscribeResult>({
                path: `/unsubscribe/resubscribe`,
                method: 'POST',
                secure: false,
                body: { token },
                type: ContentType.Json,
                format: 'json'
            })
    };

    emailPreferences = {
        get: () =>
            this.request<EmailPreference>({
                path: `/account/email-preferences`,
                method: 'GET',
                secure: true,
                format: 'json'
            }),
        set: (unsubscribed: boolean) =>
            this.request<boolean>({
                path: `/account/email-preferences`,
                method: 'PUT',
                secure: true,
                body: { unsubscribed },
                type: ContentType.Json,
                format: 'json'
            })
    };
}
